import Vue from 'vue'
import Router from 'vue-router'

import MainLayout from '@/layouts/MainLayout.vue'
import {saveLoginReturnTarget} from '@/utils/authRedirect'

Vue.use(Router)

const AdminPlaceholderView = () => import('@/views/admin/AdminPlaceholderView.vue')
const LoginView = () => import('@/views/auth/LoginView.vue')
const ChangePasswordView = () => import('@/views/auth/ChangePasswordView.vue')
const ForbiddenView = () => import('@/views/admin/ForbiddenView.vue')
const OperationLogsView = () => import('@/views/admin/OperationLogsView.vue')
const FlightPilotsView = () => import('@/views/admin/FlightPilotsView.vue')
const FlightTaskCalendarView = () => import('@/views/admin/FlightTaskCalendarView.vue')
const FlightPilotAssessmentsView = () => import('@/views/admin/FlightPilotAssessmentsView.vue')
const FlightTaskDetailView = () => import('@/views/admin/AdminFlightTaskDetailView.vue')
const OperationStandardBundlesView = () => import('@/views/admin/OperationStandardBundlesView.vue')
const OperationStandardsView = () => import('@/views/admin/OperationStandardsView.vue')
const LearningRecordsView = () => import('@/views/admin/LearningRecordsView.vue')
const MediaAssetsView = () => import('@/views/admin/MediaAssetsView.vue')
const PartnerProfilesView = () => import('@/views/admin/PartnerProfilesView.vue')
const ProjectDetailView = () => import('@/views/admin/AdminProjectDetailView.vue')
const ServiceProductsView = () => import('@/views/admin/ServiceProductsView.vue')
const ContractDetailView = () => import('@/views/admin/ContractDetailView.vue')
const QuotationDetailView = () => import('@/views/admin/QuotationDetailView.vue')
const QuotationContractsView = () => import('@/views/admin/QuotationContractsView.vue')
const ReportExportsView = () => import('@/views/admin/ReportExportsView.vue')
const PaymentPlansView = () => import('@/views/admin/PaymentPlansView.vue')
const PaymentsView = () => import('@/views/admin/PaymentsView.vue')
const InvoicesView = () => import('@/views/admin/InvoicesView.vue')
const ReconciliationView = () => import('@/views/admin/ReconciliationView.vue')
const FeedbackTicketsView = () => import('@/views/admin/FeedbackTicketsView.vue')
const NoticeTemplatesView = () => import('@/views/admin/NoticeTemplatesView.vue')
const NoticeLogsView = () => import('@/views/admin/NoticeLogsView.vue')
const WorkReportsView = () => import('@/views/admin/WorkReportsView.vue')
const BackstageNoticesView = () => import('@/views/admin/BackstageNoticesView.vue')
const PersonalCalendarView = () => import('@/views/admin/PersonalCalendarView.vue')
const SystemSettingsView = () => import('@/views/admin/SystemSettingsView.vue')
const MiniappUsersView = () => import('@/views/admin/MiniappUsersView.vue')
const WebsiteContentView = () => import('@/views/admin/WebsiteContentView.vue')

const createWorkspaceRoute = (path, platformKey, children) => ({
    path,
    component: MainLayout,
    meta: {
        platformKey,
    },
    children,
})

// 后台完整菜单先用统一占位页承接未开发模块，保证菜单、路由和权限配置不断链。
const createAdminPlaceholderRoute = (path, name, title, groupTitle, description, plannedItems) => ({
    path,
    name,
    component: AdminPlaceholderView,
    meta: {
        title,
        groupTitle,
        description,
        plannedItems,
        // 待开发入口统一显示主内容区遮罩，避免占位说明被误认为可操作页面。
        comingSoon: true,
        comingSoonTitle: '正在开发中',
        comingSoonDescription: '该功能正在开发中，当前仅开放菜单入口和范围说明。',
    },
})

const systemSettingDescriptions = {
    accounts: '维护后台账号、多角色归属、主角色、例外授权和启停状态。',
    roles: '配置角色的业务域数据范围、功能入口和按钮权限。',
    menus: '维护后台功能入口的显示名称、排序和启停状态。',
    config: '维护系统基础配置项、配置键值和启停状态。',
    dictionaries: '维护业务基础字段、字典分组和字段值。',
    resourceTypes: '维护项目接单资源项、计量单位列表和默认后续流转。',
    flightTaskMilestones: '维护飞手任务节点、顺序和影像对比类型，供新任务生成时套用。',
    environment: '查看后端端口、数据库连接和运行环境状态。',
}

const createSystemSettingRoute = (path, name, title, systemTab) => ({
    path: `system/${path}`,
    name,
    component: SystemSettingsView,
    meta: {
        title,
        systemTab,
        description: systemSettingDescriptions[systemTab],
    },
})

const router = new Router({
    mode: 'hash',
    scrollBehavior() {
        return {x: 0, y: 0}
    },
    routes: [
        {
            path: '/',
            redirect: '/admin/dashboard',
        },
        {
            path: '/login',
            name: 'login',
            component: LoginView,
            meta: {public: true, title: '登录'},
        },
        {
            path: '/change-password',
            name: 'change-password',
            component: ChangePasswordView,
            meta: {title: '设置登录密码'},
        },
        // 门户端和签约客户端不纳入后台系统壳层，旧入口统一回到后台首页。
        {path: '/portal', redirect: '/admin/dashboard'},
        {path: '/portal/*', redirect: '/admin/dashboard'},
        {path: '/client', redirect: '/admin/dashboard'},
        {path: '/client/*', redirect: '/admin/dashboard'},
        // 合作生态端与飞手端由小程序承载，旧后台 Web 地址统一回到管理后台首页。
        {path: '/partner', redirect: '/admin/dashboard'},
        {path: '/partner/*', redirect: '/admin/dashboard'},
        {path: '/pilot', redirect: '/admin/dashboard'},
        {path: '/pilot/*', redirect: '/admin/dashboard'},
        createWorkspaceRoute('/admin', 'admin', [
            {
                path: '',
                redirect: '/admin/dashboard',
            },
            {
                path: 'dashboard',
                name: 'admin-dashboard',
                component: () => import('@/views/admin/DashboardView.vue'),
                meta: {title: '工作台', description: '汇总线索、项目、接单和财务关键指标。'},
            },
            {
                path: 'forbidden',
                name: 'admin-forbidden',
                component: ForbiddenView,
                meta: {title: '无权访问', description: '当前账号没有目标页面权限。', utilityRoute: true},
            },
            {
                path: 'notices',
                name: 'admin-notices',
                component: BackstageNoticesView,
                meta: {
                    title: '站内消息',
                    description: '查看当前后台账号收到的业务通知和阅读状态。',
                    utilityRoute: true,
                },
            },
            {
                path: 'personal-calendar',
                name: 'admin-personal-calendar',
                component: PersonalCalendarView,
                meta: {
                    title: '个人日程',
                    description: '按月或按周管理当前账号自己的日程和站内提醒。',
                    utilityRoute: true,
                },
            },
            {
                path: 'leads',
                name: 'admin-leads',
                component: () => import('@/views/admin/LeadsView.vue'),
                meta: {title: '线索池', description: '集中分配、改派、回收和查看销售线索。'},
            },
            {
                path: 'my-leads',
                name: 'admin-my-leads',
                component: () => import('@/views/admin/MyLeadsView.vue'),
                meta: {title: '我的线索', description: '查看本人负责线索并推进跟进动作。'},
            },
            {
                path: 'opportunities',
                name: 'admin-opportunities',
                component: () => import('@/views/admin/OpportunitiesView.vue'),
                meta: {title: '商机管理', description: '维护独立创建或线索转化进入的商机阶段、负责人和下一步动作。'},
            },
            {
                path: 'customers',
                name: 'admin-customers',
                component: () => import('@/views/admin/CustomersView.vue'),
                meta: {title: '客户管理', description: '维护客户基础资料、等级、行业地区和负责人。'},
            },
            {
                path: 'contacts',
                redirect: '/admin/customers',
            },
            {
                path: 'partners',
                name: 'admin-partners',
                component: PartnerProfilesView,
                meta: {title: '合作生态档案', description: '沉淀合作生态资料、服务区域和合作状态。'},
            },
            {
                path: 'products',
                name: 'admin-products',
                component: ServiceProductsView,
                meta: {title: '产品与服务', description: '维护报价明细和官网展示共用的服务项、计价方式和参考单价。'},
            },
            {
                path: 'quotations',
                name: 'admin-quotations',
                component: QuotationContractsView,
                meta: {title: '报价管理', description: '维护报价单、报价状态、明细金额和客户确认进度。'},
            },
            {
                path: 'quotations/:id',
                name: 'admin-quotation-detail',
                component: QuotationDetailView,
                meta: {
                    title: '报价详情',
                    activeMenu: '/admin/quotations',
                    description: '查看报价单基础信息、报价明细、附件和客户确认进度。',
                },
            },
            {
                path: 'contracts',
                name: 'admin-contracts',
                component: QuotationContractsView,
                meta: {title: '合同管理', description: '维护合同状态、签署进度、项目转入和财务衔接。'},
            },
            {
                path: 'contracts/:id',
                name: 'admin-contract-detail',
                component: ContractDetailView,
                meta: {
                    title: '合同详情',
                    activeMenu: '/admin/contracts',
                    description: '查看合同基础信息、签署进度、审批归档和项目财务衔接。',
                },
            },
            {
                path: 'quotation-contracts',
                redirect: '/admin/quotations',
            },
            {
                path: 'projects',
                name: 'admin-projects',
                component: () => import('@/views/admin/ProjectsView.vue'),
                meta: {title: '项目管理', description: '查看项目阶段、排期、负责人和执行进度。'},
            },
            {
                path: 'projects/:id',
                name: 'admin-project-detail',
                component: ProjectDetailView,
                meta: {
                    title: '项目详情',
                    activeMenu: '/admin/projects',
                    description: '查看项目阶段、排期、负责人、回款状态和执行摘要。',
                },
            },
            {
                path: 'acceptance',
                name: 'admin-acceptance',
                component: () => import('@/views/admin/AcceptanceView.vue'),
                meta: {title: '客户验收', description: '维护客户验收状态、反馈意见和归档动作。'},
            },
            {
                path: 'project-pool',
                name: 'admin-project-pool',
                component: () => import('@/views/admin/AdminProjectPoolView.vue'),
                meta: {title: '项目池配置', description: '配置开放项目、承接规则、合作生态范围和开放时间。'},
            },
            {
                path: 'partner-applications',
                name: 'admin-partner-applications',
                component: () => import('@/views/admin/PartnerApplicationsView.vue'),
                meta: {title: '承接审核', description: '审核合作生态承接申请并生成作业任务码。'},
            },
            {
                path: 'flight-tasks',
                name: 'admin-flight-tasks',
                component: () => import('@/views/admin/FlightTasksView.vue'),
                meta: {title: '作业任务', description: '维护作业任务、执行状态和作业回传记录。'},
            },
            {
                path: 'flight-task-calendar',
                name: 'admin-flight-task-calendar',
                component: FlightTaskCalendarView,
                meta: {title: '作业日历', description: '按日期查看当前权限范围内的作业任务和执行飞手。'},
            },
            {
                path: 'flight-pilot-assessments',
                name: 'admin-flight-pilot-assessments',
                component: FlightPilotAssessmentsView,
                meta: {title: '飞手评估', description: '按历史项目等权汇总飞手任务审核评价，并查看项目和任务明细。'},
            },
            {
                path: 'flight-tasks/:id',
                name: 'admin-flight-task-detail',
                component: FlightTaskDetailView,
                meta: {
                    title: '作业任务详情',
                    activeMenu: '/admin/flight-tasks',
                    description: '查看任务码、认领飞手、作业日志、异常反馈和审核进度。',
                },
            },
            {
                path: 'flight-pilots',
                name: 'admin-flight-pilots',
                component: FlightPilotsView,
                meta: {title: '合作生态成员', description: '维护成员基础资料、所属合作生态、岗位标签、服务区域和启停状态。'},
            },
            {
                path: 'operation-standards',
                name: 'admin-operation-standards',
                component: OperationStandardsView,
                meta: {
                    title: '作业类型配置',
                    description: '维护作业类型，并固定绑定里程碑模板和规范资料库。',
                },
            },
            {
                path: 'operation-standard-bundles',
                name: 'admin-operation-standard-bundles',
                component: OperationStandardBundlesView,
                meta: {
                    title: '规范资料库',
                    description: '维护飞手作业前必须完成的学习集合、视频和文档素材。',
                },
            },
            {
                path: 'learning-records',
                name: 'admin-learning-records',
                component: LearningRecordsView,
                meta: {
                    title: '学习记录',
                    description: '查看合作生态和飞手的学习进度、已读确认、任务关联和执行证据。',
                },
            },
            {
                path: 'media-assets',
                name: 'admin-media-assets',
                component: MediaAssetsView,
                meta: {
                    title: '项目影像',
                    description: '按项目和作业批次汇总交付影像、验收成果及客户可见资料包。',
                },
            },
            {path: 'payment-plans', name: 'admin-payment-plans', component: PaymentPlansView, meta: {title: '回款计划', description: '销售按合同一次维护全部分期，合计校验通过后保存即生效。'}},
            {path: 'payments', name: 'admin-payments', component: PaymentsView, meta: {title: '回款台账', description: '财务直接确认到账；错误记录填写原因作废后重新登记。'}},
            {path: 'invoices', name: 'admin-invoices', component: InvoicesView, meta: {title: '开票管理', description: '处理客户开票申请，直接上传或分次开具发票并安全回传附件。'}},
            {path: 'reconciliation', name: 'admin-reconciliation', component: ReconciliationView, meta: {title: '对账报表', description: '按条件预览金额口径并生成四工作表正式 XLSX。'}},
            {path: 'notice-templates', name: 'admin-notice-templates', component: NoticeTemplatesView, meta: {title: '通知模板', groupTitle: '通知与报表', description: '维护固定业务事件的站内、小程序、短信与服务号通知模板。'}},
            {path: 'notice-logs', name: 'admin-notice-logs', component: NoticeLogsView, meta: {title: '发送日志', groupTitle: '通知与报表', description: '查看通知通道状态、失败原因、请求 ID 与人工重试留痕。'}},
            // 工作汇总是通知与报表下的真实入口，必须在通配回退前完成注册。
            {path: 'work-reports', name: 'admin-work-reports', component: WorkReportsView, meta: {title: '工作汇总', groupTitle: '通知与报表', description: '自动汇总业务工作，支持日报、可提交周报、图片和点评沟通。'}},
            {
                path: 'feedback-tickets',
                name: 'admin-feedback-tickets',
                component: FeedbackTicketsView,
                meta: {
                    title: '问题反馈',
                    description: '处理客户、合作生态和飞手提交的问题反馈，回复后可继续跟进或关闭。',
                },
            },
            {
                path: 'report-exports',
                name: 'admin-report-exports',
                component: ReportExportsView,
                meta: {title: '报表导出', description: '按线索、商机、合同、项目、财务、渠道和飞手输出运营 CSV 报表。'},
            },
            {
                path: 'case-library',
                name: 'admin-case-library',
                component: WebsiteContentView,
                meta: {title: '官网内容', description: '维护门户站展示内容、案例发布状态和官网素材。'},
            },
            {
                path: 'operation-logs',
                name: 'admin-operation-logs',
                component: OperationLogsView,
                meta: {title: '操作日志', description: '查看关键业务、菜单权限和系统配置变更的操作留痕。'},
            },
            {
                path: 'system-settings',
                redirect: '/admin/system/config',
            },
            {
                path: 'system',
                redirect: '/admin/system/config',
            },
            createSystemSettingRoute('accounts', 'admin-system-accounts', '成员与账号', 'accounts'),
            {
                path: 'system/miniapp-users',
                name: 'admin-system-miniapp-users',
                component: MiniappUsersView,
                meta: {
                    title: '小程序用户',
                    description: '查看微信账户、角色绑定、飞手所属合作生态，并维护邀请口令和解绑操作。',
                },
            },
            createSystemSettingRoute('roles', 'admin-system-roles', '角色权限', 'roles'),
            createSystemSettingRoute('menus', 'admin-system-menus', '菜单管理', 'menus'),
            createSystemSettingRoute('config', 'admin-system-config', '系统配置', 'config'),
            createSystemSettingRoute('dictionaries', 'admin-system-dictionaries', '业务字典', 'dictionaries'),
            createSystemSettingRoute('resource-types', 'admin-system-resource-types', '资源项配置', 'resourceTypes'),
            createSystemSettingRoute('flight-task-milestone-templates', 'admin-system-flight-task-milestones', '任务里程碑模板', 'flightTaskMilestones'),
            createSystemSettingRoute('environment', 'admin-system-environment', '运行环境', 'environment'),
        ]),
        {
            path: '*',
            redirect: '/admin/dashboard',
        },
    ],
})

router.beforeEach((to, from, next) => {
    // 记录最后访问的后台页面，兼容快捷账号弹窗在 /login 无 redirect 参数时的回跳。
    if (to.path === '/admin' || to.path.startsWith('/admin/')) {
        saveLoginReturnTarget(to.fullPath)
    }
    next()
})

export default router
